/**
 * Pure helpers for classifying a failed bank connection. Deliberately their own module rather
 * than living in BankConnectionDialog.tsx: a component file that also exports functions breaks
 * Fast Refresh (react-refresh/only-export-components), and keeping them here means they can be
 * tested directly instead of only through whichever component path happens to reach them.
 */

/**
 * The backend signals these conditions on either `error` or `message` depending on whether the
 * failure came back as a structured edge-function body or a thrown Error, so both are checked.
 * One home for the casts, instead of one per call site.
 */
type StructuredServiceError = { error?: string; message?: string; details?: { companyId?: string } };

function isServiceErrorCode(error: unknown, code: string): boolean {
  const e = error as StructuredServiceError | null | undefined;
  return e?.error === code || e?.message === code;
}

function kycCompanyIdFrom(error: unknown): string | undefined {
  return (error as StructuredServiceError | null | undefined)?.details?.companyId;
}

/**
 * What a failed getBankAccountsToConnect actually means, decided in one place. KYC only counts
 * when the error carries the companyId the modal needs -- without it there is nothing to open,
 * so it falls through to the generic message rather than half-opening a broken dialog.
 */
export type ConnectBankFailure =
  | { kind: 'kyc'; companyId: string }
  | { kind: 'consent' }
  | { kind: 'generic' };

export function classifyConnectBankFailure(error: unknown): ConnectBankFailure {
  const companyId = kycCompanyIdFrom(error);
  if (isServiceErrorCode(error, 'KYC_REQUIRED') && companyId) {
    return { kind: 'kyc', companyId };
  }
  if (isServiceErrorCode(error, 'CONSENT_REQUIRED')) {
    return { kind: 'consent' };
  }
  return { kind: 'generic' };
}
